import {
    BudgetWarningAckAttributes,
    extrapolateMonthlyMap,
    horizonFillMonths,
    horizonTargetMonths,
    round2,
} from '@callmart/types';
import { BillingConfigWithRelations } from '../../services/budgetBillingConfig';

export interface CalculatedRow {
    billingConfigId: number;
    month: string;
    forecastVolume: number;
    calculatedCost: number;
    // CALL-752: true when this month's cost used a forward-extrapolated rate (price or ABT). Drives
    // the blue cell highlight in the Kosten view. Always false for flat-fee rows (cost = bonus).
    extrapolated: boolean;
}

export interface CalculatedConfig {
    config: BillingConfigWithRelations;
    monthlyResults: Map<string, CalculatedRow>;
}

// CALL-783: `round2` used to be a hand-copied one-liner here. It now comes from
// @callmart/types/calculations/rounding — the same module the backend cost path rounds with — for
// exactly the reason stated below: the client preview must never disagree with the persisted server
// result, and a rounding fix applied to only one of two copies desyncs them silently.

// CALL-752: extrapolateMonthlyMap + horizonTargetMonths are the single shared implementation in
// @callmart/types/calculations/budgetExtrapolation, used verbatim by the backend cost path too, so
// the client preview can never silently disagree with the persisted server result. Forward-fill is
// bounded to a 6-month horizon past the last entered price (not the calendar year) — the budget keeps
// calculating past the last entered rate, but a lone future entry can't fill a whole year.

// CALL-752: 'YYYY-MM-01' month-key helpers (lexicographic order == chronological for this format).
const monthAfter = (month: string): string => {
    const year = Number(month.slice(0, 4));
    const m = Number(month.slice(5, 7));
    return m === 12 ? `${year + 1}-01-01` : `${year}-${String(m + 1).padStart(2, '0')}-01`;
};
const contiguousMonths = (startInclusive: string, endInclusive: string): string[] => {
    const out: string[] = [];
    for (let cur = startInclusive; cur <= endInclusive; cur = monthAfter(cur)) out.push(cur);
    return out;
};

/**
 * CALL-752: build the values an entry grid (Minutenpreise / Vertrags-ABT) shows for one config —
 * the entered monthly rates plus the forward-extrapolated future cells. Returns the value per month
 * and the months that are extrapolated (display-only — styled blue, never saved as entered values).
 *
 * The forward fill runs over a CONTIGUOUS range from the month after the last entered value through
 * the end of the visible window, so the 3-month rolling feedback is complete and the displayed values
 * don't depend on where the window is scrolled — then bounded to the 6-month horizon past the last
 * entered price via horizonTargetMonths, exactly like the cost path (calculateAllCosts / backend
 * computeHandlingCosts), so the grid shows precisely what the budget charges.
 *
 * `anchorMap` is the horizon anchor: the Vertrags-ABT grid passes the minute-price map (the cost path
 * gates ABT extrapolation on prices, so the ABT shown matches what's charged); it defaults to the
 * entered map for the Minutenpreise grid.
 */
export const buildExtrapolatedRow = (
    enteredMap: Map<string, number>,
    windowMonths: string[],
    anchorMap: Map<string, number> = enteredMap
): { values: Map<string, number>; extrapolatedMonths: Set<string> } => {
    const entered = [...enteredMap.keys()].sort();
    if (entered.length === 0 || windowMonths.length === 0) {
        return { values: new Map(enteredMap), extrapolatedMonths: new Set() };
    }

    const lastEntered = entered[entered.length - 1];
    const windowEnd = [...windowMonths].sort()[windowMonths.length - 1];
    const range = contiguousMonths(monthAfter(lastEntered), windowEnd);
    const targetMonths = horizonTargetMonths(anchorMap, range);

    const { filled, extrapolatedMonths } = extrapolateMonthlyMap(enteredMap, targetMonths);
    return { values: filled, extrapolatedMonths: new Set(extrapolatedMonths) };
};

export const calculateRowMonthCost = ({
    forecastVolume,
    abtSeconds,
    minutePrice,
    serviceFactor,
    bonus,
    flatFee,
}: {
    forecastVolume: number;
    abtSeconds: number;
    minutePrice: number;
    serviceFactor: number;
    bonus: number;
    flatFee: boolean;
}): number => {
    if (flatFee) return round2(bonus);
    return round2(forecastVolume * (abtSeconds / 60) * minutePrice * serviceFactor + bonus);
};

export const calculateAllCosts = (
    configs: BillingConfigWithRelations[],
    options: { extrapolate?: boolean } = {}
): CalculatedConfig[] => {
    // CALL-752: the Ist-Budget "Mit Extrapolation" toggle can turn extrapolation off to show the
    // real entered-rate costs only. With it off, no months are forward-filled and nothing is flagged
    // extrapolated (so no blue) — months past the last entered rate simply cost 0.
    const { extrapolate = true } = options;
    return configs.map((config) => {
        const minutePriceMap = new Map((config.minutePrices || []).map((mp) => [mp.month, Number(mp.price)]));
        const abtMap = new Map((config.contractABTs || []).map((abt) => [abt.month, Number(abt.abtSeconds)]));
        const bonusMap = new Map((config.bonuses || []).map((b) => [b.month, Number(b.amount)]));
        const volumeMap = new Map((config.forecastVolumes || []).map((fv) => [fv.month, Number(fv.volume)]));

        // CALL-752: forward-extrapolate empty future prices / ABT over the forecast-volume months,
        // bounded to a 6-month horizon past the last entered price (horizonTargetMonths) — identical
        // to the backend computeHandlingCosts, so the on-screen Kosten and the exported/committed
        // "Kosten gesamt" agree to the cent. Empty target months (extrapolate off) => entered rates only.
        const targetMonths = extrapolate ? horizonTargetMonths(minutePriceMap, [...volumeMap.keys()]) : [];
        const { filled: priceMap } = extrapolateMonthlyMap(minutePriceMap, targetMonths);
        const { filled: abtFilledMap } = extrapolateMonthlyMap(abtMap, targetMonths);
        // CALL-752: forward-extrapolate forecast volumes too (Handlingdaten), so a month the budget
        // still prices but where the Strategic-Volume data ran out gets a projected volume instead of
        // 0 — the cost then continues with projected volume x projected rate, like Strat FC. Bounded
        // to the same 6-month horizon past the last real volume; never widens the month set / period.
        const volumeFillMonths = extrapolate ? horizonFillMonths(volumeMap) : [];
        const { filled: volumeFilledMap } = extrapolateMonthlyMap(volumeMap, volumeFillMonths);

        const allMonths = [
            ...new Set([...priceMap.keys(), ...abtFilledMap.keys(), ...bonusMap.keys(), ...volumeMap.keys()]),
        ].sort();

        const monthlyResults = new Map<string, CalculatedRow>();
        const flatFee = Boolean(config.flatFee);

        for (const month of allMonths) {
            const forecastVolume = volumeFilledMap.get(month) ?? 0;
            const abtSeconds = abtFilledMap.get(month) ?? config.defaultAbt ?? 0;
            // CALL-824: entered/extrapolated price, else the Standard-Minutenpreis, else 0 (same as BE).
            const minutePrice = priceMap.get(month) ?? config.defaultMinutePrice ?? 0;
            const bonus = bonusMap.get(month) ?? 0;
            // CALL-752: the cost cell is extrapolated when any input was carried forward — price, ABT,
            // or forecast volume present in the filled map but not in the entered map. Never for
            // flat-fee rows, whose cost is the entered bonus and isn't affected by extrapolation.
            const extrapolated =
                !flatFee &&
                ((priceMap.has(month) && !minutePriceMap.has(month)) ||
                    (abtFilledMap.has(month) && !abtMap.has(month)) ||
                    (volumeFilledMap.has(month) && !volumeMap.has(month)));

            monthlyResults.set(month, {
                billingConfigId: config.id,
                month,
                forecastVolume,
                calculatedCost: calculateRowMonthCost({
                    forecastVolume,
                    abtSeconds,
                    minutePrice,
                    serviceFactor: Number(config.serviceFactor ?? 1),
                    bonus,
                    flatFee,
                }),
                extrapolated,
            });
        }

        return { config, monthlyResults };
    });
};

export const calculateMonthlyCostTotals = (calculated: CalculatedConfig[]): Map<string, number> => {
    const totals = new Map<string, number>();
    for (const { monthlyResults } of calculated) {
        for (const [month, row] of monthlyResults) {
            totals.set(month, (totals.get(month) ?? 0) + row.calculatedCost);
        }
    }
    return totals;
};

export type RowCompletenessStatus = 'complete' | 'partial' | 'empty';

export interface RowCompleteness {
    status: RowCompletenessStatus;
    monthsTotal: number;
    monthsWithCost: number;
    missingPrice: number;
    missingAbt: number;
    missingVolume: number;
    missingBonus: number;
    flatFee: boolean;
    // CALL-816: the month keys behind the counts above — what an acknowledgement freezes.
    missingMonths: string[];
}

export const getRowCompleteness = (
    config: BillingConfigWithRelations,
    activeMonths: string[],
    extrapolate = true
): RowCompleteness => {
    const enteredPriceMap = new Map((config.minutePrices || []).map((mp) => [mp.month, Number(mp.price)]));
    const enteredAbtMap = new Map((config.contractABTs || []).map((abt) => [abt.month, Number(abt.abtSeconds)]));
    const bonusMap = new Map((config.bonuses || []).map((b) => [b.month, Number(b.amount)]));
    const volumeMap = new Map((config.forecastVolumes || []).map((fv) => [fv.month, Number(fv.volume)]));
    const defaultAbt = Number(config.defaultAbt ?? 0);
    // CALL-824: null = no fallback, a typed 0 is a real (free) price — so price availability is
    // PRESENCE (entered or default), unlike ABT, where only a positive value is valid.
    const defaultMinutePrice = config.defaultMinutePrice == null ? null : Number(config.defaultMinutePrice);
    const flatFee = Boolean(config.flatFee);

    // CALL-752: count extrapolated future prices/ABT/volumes as present (over the same 6-month horizon
    // as the Kosten/detail cost path) so window months carrying an extrapolated value aren't flagged
    // "missing price/volume"/partial. With extrapolation OFF the status must use entered rates only —
    // otherwise a month covered solely by an extrapolated rate (e.g. prices Jan–Jun, auto volumes to
    // Dec) reads "complete" while its Kosten cell and the export show 0.
    const targetMonths = extrapolate ? horizonTargetMonths(enteredPriceMap, [...volumeMap.keys()]) : [];
    const { filled: priceMap } = extrapolateMonthlyMap(enteredPriceMap, targetMonths);
    const { filled: abtMap } = extrapolateMonthlyMap(enteredAbtMap, targetMonths);
    const { filled: volumeFilledMap } = extrapolateMonthlyMap(
        volumeMap,
        extrapolate ? horizonFillMonths(volumeMap) : []
    );

    let monthsWithCost = 0;
    let missingPrice = 0;
    let missingAbt = 0;
    let missingVolume = 0;
    let missingBonus = 0;
    let completeMonths = 0;
    let monthsWithAnyData = 0;
    const missingMonths: string[] = [];

    for (const month of activeMonths) {
        if (flatFee) {
            const bonus = bonusMap.get(month) ?? 0;
            if (bonus > 0) monthsWithCost += 1;
            else {
                missingBonus += 1;
                missingMonths.push(month);
            }
            continue;
        }

        const availablePrice = priceMap.get(month) ?? defaultMinutePrice;
        const price = availablePrice ?? 0;
        const abt = abtMap.get(month) ?? defaultAbt;
        const volume = volumeFilledMap.get(month) ?? 0;
        const bonus = bonusMap.get(month) ?? 0;

        const priceOk = availablePrice !== null;
        const abtOk = abt > 0;
        const volumeOk = volume > 0;
        const calculatedCost = calculateRowMonthCost({
            forecastVolume: volume,
            abtSeconds: abt,
            minutePrice: price,
            serviceFactor: Number(config.serviceFactor ?? 1),
            bonus,
            flatFee,
        });

        if (priceOk && abtOk && volumeOk) {
            completeMonths += 1;
        } else {
            if (!priceOk) missingPrice += 1;
            if (!abtOk) missingAbt += 1;
            if (!volumeOk) missingVolume += 1;
            missingMonths.push(month);
        }

        if (calculatedCost > 0) {
            monthsWithCost += 1;
        }
        if (price > 0 || abt > 0 || volume > 0 || bonus > 0) {
            monthsWithAnyData += 1;
        }
    }

    const monthsTotal = activeMonths.length;
    let status: RowCompletenessStatus;
    if (flatFee) {
        if (monthsWithCost === 0) status = 'empty';
        else if (monthsWithCost === monthsTotal) status = 'complete';
        else status = 'partial';
    } else {
        if (completeMonths === monthsTotal && monthsTotal > 0) {
            status = 'complete';
        } else if (monthsWithCost > 0 || monthsWithAnyData > 0) {
            status = 'partial';
        } else {
            status = 'empty';
        }
    }

    return {
        status,
        monthsTotal,
        monthsWithCost,
        missingPrice,
        missingAbt,
        missingVolume,
        missingBonus,
        flatFee,
        missingMonths,
    };
};

/** CALL-816: an acknowledgement keeps covering a row while the row's CURRENT missing months stay a
 *  subset of the months frozen at ack time — entering data never resurfaces the warning, a NEWLY
 *  missing month (deleted data, extended period) does. An empty current set is trivially covered. */
export const isAckCovered = (currentMissing: string[], ackedMissing: string[]): boolean => {
    const acked = new Set(ackedMissing);
    return currentMissing.every((month) => acked.has(month));
};

/** CALL-816: a row's acknowledgement state, carried on the Overview row next to `status`.
 *  `canonicalMissing` is the row's missing months over the WHOLE budget period with extrapolation
 *  OFF — deliberately independent of the month window, the date range and the extrapolation toggle,
 *  so what was acknowledged cannot change by scrolling or toggling the view. */
export interface RowAckInfo {
    ack?: BudgetWarningAckAttributes;
    canonicalMissing: string[];
    covered: boolean;
}
